import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Ban, ChevronDown, Loader2 } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  useBlockAvailabilityForm,
  toWireRequest,
} from './block-availability-form-state';
import { BlockAvailabilityDateStrip } from './block-availability-date-strip';
import { BlockAvailabilityProviderSearch } from './block-availability-provider-search';
import { BlockAvailabilityDeviceSearch } from './block-availability-device-search';
import { BlockAvailabilityBreakForm } from './block-availability-break-form';
import { BlockAvailabilityRecurringPicker } from './block-availability-recurring-picker';
import { BlockersConflictDialog } from './block-availability-blockers-dialog';
import { validateBlockForm } from './block-availability-validation';
import { useSaveBlockSlot, type BlockSlotConflict } from '@/scheduling/services/use-save-block-slot';
import { useSaveBlockResourceSlot } from '@/scheduling/services/use-save-block-resource-slot';
import { useBlockAppointment } from '@/scheduling/services/use-block-appointment';
import { useBlockResourceAppointment } from '@/scheduling/services/use-block-resource-appointment';
import { useUnblockSlot } from '@/scheduling/services/use-unblock-slot';
import { useUnblockResourceSlot } from '@/scheduling/services/use-unblock-resource-slot';
import { usePermission } from '@/auth/permission-gate';
import { Switch } from '@/components/ui/switch';
import {
  BLOCK_ACTOR_DEVICE,
  BLOCK_ACTOR_PRACTITIONER,
  type BlockActorKind,
} from '@/types/block-availability-api';
import type { CalendarDevice } from '@/types/scheduling-api';

/**
 * BlockAvailabilityPanel — Block (inline left panel)
 *
 * Flutter parity:
 *   `block_availability_widget.dart` (791 LOC).
 *
 * Mounts at the same anchor as CreateAppointmentPanel and follows the
 * same geometry gate (`use-booking-rail-layout.ts`): classic 380px on
 * the LEFT / wide clamp(420px,33vw,560px) on the RIGHT, matching the
 * create rail so the two never disagree about side or width.
 *
 * PR 3 scope (PROD-824): break form (REASON + start/end time) + save
 * chain wiring (`useSaveBlockSlot` hook posts to
 * `/api/Scheduling/BlockSlot`). Recurring picker = PR 4. Edit /
 * Unblock = PR 5.
 *
 * Permission gate handled at the parent dropdown (Samer §2 on PR 1,
 * `calendar-toolbar.tsx`) — the panel itself trusts whoever
 * mounted it had `Doctor_Calender`.
 *
 * PROD-2454 — a DEVICE can be blocked through this same panel. What changed
 * is the actor picker and the feed behind the break form's shift list; the
 * date strip, the break form, the recurrence picker, the save confirmation,
 * the blockers dialog and the unblock actions are untouched, because none of
 * them was ever practitioner-specific once an actor id was in hand. The wire
 * needed nothing at all: `POST /api/Scheduling/BlockSlot` has always taken
 * `actorId`.
 *
 * BACKEND DEPENDENCY (PROD-2451): `BlockSlotCommand` resolved a schedule only
 * by `IsDefault = 1`, which no device schedule carries, so a device block
 * answered 400 "Can't find default schedule for actor". This screen's rule is
 * that a control never appears unless it works, and that is enforced here by
 * MERGE ORDER rather than by a flag — the Devices tab's Block entry point
 * ships only after that fix is on dev.
 */

interface BlockAvailabilityPanelProps {
  open: boolean;
  onClose: () => void;
  /** Org GUID for provider list scoping. */
  organizationId: string;
  /** Practice GUID — drives the provider list AND the wire request's
   *  `practiceId` field. */
  practiceId: string;
  /**
   * When set, the panel loads the existing block via
   * `GET /api/Appointment/BlockAppointment?id={blockId}` and renders
   * in edit mode (form fields display-only, header label = "Save"
   * instead of "Create", Unblock buttons surface). Flutter parity:
   * `block_availability_widget.dart:82-86` (`widget.id`).
   */
  blockId?: string | null;
  /**
   * WHICH kind of actor this panel blocks (PROD-2454). Default
   * `'practitioner'` — every existing caller keeps the behaviour it had.
   *
   * Decided by the ENTRY POINT, not by a control inside the panel: the
   * Practitioners tab's `Create ▸ Block Availability` opens a practitioner
   * panel, the Devices tab's `Create ▸ Block a device` opens a device one,
   * exactly as that same menu already decides New Appointment vs Book a
   * device.
   *
   * Rendering both pickers at once was the other option and was rejected.
   * The form has ONE actor slot (`BlockActor`), so two visible pickers can
   * represent a state the request cannot — one of each selected — and the
   * panel would have to police it. That is the same invariant `activePanel`
   * enforces one rail over, and it is cheaper to make unrepresentable than
   * to guard. The device picker is a SIBLING of the provider one
   * (`block-availability-device-search.tsx` beside
   * `block-availability-provider-search.tsx`, same slot, same contract), not
   * a second control competing with it.
   *
   * PROD-2515 — THE PANEL MUST BE REMOUNTED WHEN THIS CHANGES, and the caller
   * is what guarantees that (`key` at the call site). This is a constructor
   * argument all the way down to `createInitialState`, and this component stays
   * mounted for the whole session inside `FrameScheduling`, so without the
   * remount the prop moved and nothing read it: a device block ran as a
   * practitioner one, `useActorShifts` asked `/api/Doctor/Presence` for a
   * machine, and the rail showed "No Slots Available" with no way to proceed.
   */
  actorKind?: BlockActorKind;
  /**
   * The date the rail opens on — the calendar's selected date (PROD-2515).
   *
   * Read once, at construction, like `actorKind`, and for the same reason: the
   * date is then the operator's to change inside the form. Omitted, it is
   * today, which is only ever right by coincidence — the Block rail used to
   * open on today while the calendar behind it was on another day, where the
   * Book-a-device rail beside it has always opened on the selected one.
   */
  initialDate?: Date;
  /**
   * The blockable devices of the practice — required when
   * `actorKind === 'device'`, ignored otherwise. Passed in rather than
   * fetched because the blockable narrowing needs both the registry and the
   * slots feed and only `SchedulingPage` holds both; see
   * `BlockAvailabilityDeviceSearch`.
   */
  devices?: readonly CalendarDevice[];
  /** True while the device feeds are still resolving. */
  devicesLoading?: boolean;
  /** Same contract as CreateAppointmentPanel — see its prop docs. */
  edge?: 'left' | 'right';
  /** Same contract as CreateAppointmentPanel — see its prop docs. */
  layout?: 'classic' | 'wide';
}

function formatDate(d: Date): string {
  return d.toLocaleDateString(undefined, { month: 'short', day: '2-digit', year: 'numeric' });
}

export function BlockAvailabilityPanel({
  open,
  onClose,
  organizationId,
  practiceId,
  blockId = null,
  actorKind = BLOCK_ACTOR_PRACTITIONER,
  initialDate,
  devices = [],
  devicesLoading = false,
  edge = 'right',
  layout = 'wide',
}: BlockAvailabilityPanelProps) {
  const isDeviceBlock = actorKind === BLOCK_ACTOR_DEVICE;
  const {
    state,
    setBlockDate,
    selectActor,
    clearActor,
    setShiftKey,
    setStartTime,
    setEndTime,
    setComment,
    setRecurringEnabled,
    setRecurring,
    setRecurringPeriod,
    toggleRecurringDay,
    hydrateFromRecord,
    reset,
  } = useBlockAvailabilityForm(actorKind, initialDate);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [blockers, setBlockers] = useState<BlockSlotConflict[] | null>(null);
  /**
   * WHICH block endpoint this panel posts to (PROD-2510). The two routes are
   * mirrors — same six-key body, one handler underneath — and each REFUSES the
   * other's actor kind with a 400 before writing anything, so this choice is
   * load-bearing rather than cosmetic.
   *
   * Driven by `state.actorKind`, the same discriminant that already picks the
   * actor picker, the Save tooltip's wording and the shift feed. Reading
   * anything else here — the selected actor's id shape, the active tab — would
   * be a second source of truth for a question the form has already answered,
   * and the two could disagree for a render.
   *
   * BOTH hooks are called unconditionally and one is chosen, never called
   * conditionally: hook order has to be stable across a kind switch, the same
   * rule `useActorShifts` follows one layer down. A mutation hook that is never
   * `.mutate`d costs nothing.
   */
  const saveProviderBlock = useSaveBlockSlot();
  const saveDeviceBlock = useSaveBlockResourceSlot();
  const saveMutation = isDeviceBlock ? saveDeviceBlock : saveProviderBlock;

  /**
   * Edit-mode hydration — Flutter `:82-86`. Fetch the existing record when
   * blockId is set, then hydrate the form state onto first successful load.
   * Subsequent loads (rare — same id) re-hydrate.
   *
   * WHICH hydration endpoint (PROD-2512), from `state.actorKind` and nothing
   * else — the same discriminant that already picks the save route, the unblock
   * route, the actor picker and the shift feed. It is load-bearing for the same
   * reason they are: the two routes refuse each other's actor kind with a 400,
   * and before the device route existed a device block answered a bare 500 here
   * — an error toast over an empty form, which is the whole ticket.
   *
   * Both hooks are called on every render, never conditionally, so hook order
   * is stable across a kind switch — the rule `useActorShifts` follows one
   * layer down. Unlike the mutation hooks above, though, a query hook that is
   * merely "not used" would still FETCH, so the unchosen one is handed a null
   * id: that is what `enabled` keys off, and it is what keeps exactly one
   * request in flight instead of one guaranteed 400 beside every good read.
   */
  const providerBlockQuery = useBlockAppointment(isDeviceBlock ? null : blockId);
  const deviceBlockQuery = useBlockResourceAppointment(isDeviceBlock ? blockId : null);
  const blockQuery = isDeviceBlock ? deviceBlockQuery : providerBlockQuery;
  const hydratedIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!blockQuery.data || !blockId) return;
    if (hydratedIdRef.current === blockId) return;
    hydrateFromRecord(blockQuery.data);
    hydratedIdRef.current = blockId;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blockQuery.data, blockId]);
  // Reset hydrated marker when the panel closes/re-opens for a
  // different id, so the next mount re-hydrates rather than skipping.
  useEffect(() => {
    if (!open) hydratedIdRef.current = null;
  }, [open]);

  const isEditMode = blockId !== null;
  const isHydrating = isEditMode && blockQuery.isLoading;

  // PROD-968 (defense in depth): even though the frame gates panel-open,
  // gate the mutating actions on `Doctor_Calender` too — create on `add`,
  // unblock on `edit` (mirrors Flutter's add-for-create / edit-for-block-edit
  // split). A user reaching the panel without the permission sees the action
  // disabled rather than a 403 round-trip.
  const canCreateBlock = usePermission(['Doctor_Calender'], { add: true });
  const canEditBlock = usePermission(['Doctor_Calender'], { edit: true });

  // Unblock — wired in PR 5 once the curl was captured (2026-05-19). Split by
  // actor kind in PROD-2510, exactly as the save above and for the same
  // reason: the provider route refuses a device-owned slot with a 400. It
  // matters MORE here than on the way in, because the device unblock route is
  // also the only one that names the device on the SignalR broadcast — the
  // provider route omits the participant entirely, which on a calendar keyed
  // by device leaves the removed tile painted.
  const unblockProviderBlock = useUnblockSlot();
  const unblockDeviceBlock = useUnblockResourceSlot();
  const unblockMutation = isDeviceBlock ? unblockDeviceBlock : unblockProviderBlock;
  const [unblockConfirm, setUnblockConfirm] = useState<null | 'single' | 'recurring'>(null);
  const isUnblocking = unblockMutation.isPending;

  // 60-day window — Samer review §2 on PR 2.
  // Aligning popover bounds with the strip's actual range so every
  // popover-selectable day appears on the strip. Intentional
  // divergence from Flutter's literal `today-1 .. today+60` bounds.
  const firstDate = new Date();
  firstDate.setHours(0, 0, 0, 0);
  const lastDate = new Date();
  lastDate.setHours(0, 0, 0, 0);
  lastDate.setDate(lastDate.getDate() + 59);

  const validation = validateBlockForm(state);
  const isSaving = saveMutation.isPending;
  const canSave = validation.ok && !isSaving && canCreateBlock;

  // Header Create button → opens the confirmation dialog. Flutter
  // shows the same dialog before posting (`block_availability_widget.dart:213-244`):
  // "Do you want to block this availability? Please make sure you
  //  cancel all the appointments booked under this time so that the
  //  patients are notified about this change".
  const handleSaveRequested = () => {
    if (!validation.ok || !canCreateBlock) return;
    setConfirmOpen(true);
  };

  // Dialog "Yes" → POST /api/Scheduling/BlockSlot.
  const handleConfirmSave = () => {
    setConfirmOpen(false);
    const body = toWireRequest(state, practiceId);
    if (!body) return; // validation already guards
    saveMutation.mutate(body, {
      onSuccess: (resp) => {
        // The server returns `blockers[]` when existing appointments
        // overlap the block — and in that case the block is NOT saved.
        // Flutter parity: shows a blockers-list dialog so the operator
        // can go reschedule/cancel those appointments first, then retry.
        // Source: `block_availability_widget.dart` blockers-state handler.
        if (resp.blockers && resp.blockers.length > 0) {
          setBlockers(resp.blockers);
          return;
        }
        toast.success('Availability blocked.');
        reset();
        onClose();
      },
      // Errors: global MutationCache onError surfaces 4xx/5xx via
      // `showServerError`. See memory `feedback_no_inline_errors_use_toast.md`.
    });
  };

  const handleCancel = () => {
    reset();
    onClose();
  };

  const handleConfirmUnblock = () => {
    if (!blockId || !canEditBlock) return;
    // `recurringId` semantics from the verified UnblockSlot curl:
    //   null       → unblock this single occurrence.
    //   <recurring.id> → unblock this and all following.
    // Flutter parity: `block_availability_widget.dart:521-562`.
    const recurringId =
      unblockConfirm === 'recurring' && state.recurring?.id ? state.recurring.id : null;
    const variant = unblockConfirm;
    setUnblockConfirm(null);
    unblockMutation.mutate(
      { id: blockId, practiceId, recurringId },
      {
        onSuccess: () => {
          toast.success(
            variant === 'recurring'
              ? 'Recurring availability unblocked.'
              : 'Availability unblocked.',
          );
          reset();
          onClose();
        },
        // Errors: global MutationCache onError surfaces 4xx/5xx via
        // `showServerError` — no inline UI needed (see memory
        // `feedback_no_inline_errors_use_toast.md`).
      },
    );
  };

  if (!open) return null;

  return (
    <div
      className={cn(
        'flex-shrink-0 border-[var(--surface-line)] bg-[var(--surface-read)] flex flex-col h-full overflow-hidden',
        layout === 'wide' ? 'w-[clamp(424px,33vw,560px)]' : 'w-[384px]',
        edge === 'right' ? 'border-l' : 'border-r',
      )}
    >
      {/* Header — Flutter `TopTitleButtonBar` (`block_availability_widget.dart:209-258`).
          Cancel (left) · title (center) · Save/Create (right). Three equal
          flex tracks so the title stays centered regardless of button width. */}
      <div className="h-12 flex-shrink-0 border-b border-[var(--surface-line)] px-3 flex items-center gap-2">
        <div className="flex-1 flex justify-start">
          <button
            onClick={handleCancel}
            disabled={isSaving}
            className="px-3 h-8 text-sm font-medium text-text-muted hover:text-text-primary disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
        <div className="flex items-center justify-center gap-2 min-w-0">
          <Ban className="h-4 w-4 text-text-muted flex-shrink-0" />
          {/* PROD-2515: the heading names the flow the operator chose. "Block
              Availability" over a machine's picker is the provider heading left
              on a device rail — the same small lie as calling the actor
              "Provider" there, which PROD-2454 already fixed one row down. The
              menu item that opens this says "Block a device"; the rail says so
              too. */}
          <span className="text-sm font-semibold text-text-primary truncate">
            {isDeviceBlock ? 'Block a Device' : 'Block Availability'}
          </span>
        </div>
        {/* Save (edit mode: disabled-grey, Flutter parity with the
            target screenshot) / Create (create mode: brand-primary). */}
        <div className="flex-1 flex justify-end">
          <button
            onClick={handleSaveRequested}
            disabled={isEditMode || !canSave}
            title={validation.ok ? undefined : validation.reason}
            className={
              isEditMode
                ? 'px-3 h-8 text-sm font-medium text-text-hint cursor-not-allowed'
                : 'px-3 h-8 rounded-md bg-brand-primary text-white text-sm font-medium hover:bg-interactive-hover disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1'
            }
          >
            {isSaving && <Loader2 className="h-3 w-3 animate-spin" />}
            {isEditMode ? 'Save' : 'Create'}
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-auto scrollbar-hide px-4 py-4 space-y-6">
        {isHydrating ? (
          <div className="space-y-3">
            <div className="h-6 w-32 rounded bg-grey-100 animate-pulse mx-auto" />
            <div className="h-[88px] rounded-card bg-grey-100 animate-pulse" />
            <div className="h-16 rounded-card bg-grey-100 animate-pulse" />
          </div>
        ) : (
        <>
        {/* Date display + strip + provider — Flutter wraps these in
            `IgnorePointer(ignoring: blockAvailability?.id != null)` at
            `block_availability_widget.dart:272-273`. Edit mode disables
            them so the user can't change the date/provider; they can
            only update REASON (the field stays interactive). */}
        <fieldset disabled={isEditMode} className="contents">
        <div className="flex items-center justify-center">
          <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
            <PopoverTrigger
              disabled={isEditMode}
              className="flex items-center gap-2 text-sm font-semibold text-text-primary hover:text-brand-primary transition-colors disabled:opacity-60 disabled:hover:text-text-primary disabled:cursor-not-allowed"
            >
              <span>{formatDate(state.blockDate)}</span>
              {!isEditMode && <ChevronDown className="h-4 w-4 text-brand-primary" />}
            </PopoverTrigger>
            <PopoverContent className="w-auto p-2" align="center">
              <Calendar
                mode="single"
                selected={state.blockDate}
                onSelect={(d) => {
                  if (d) {
                    setBlockDate(d);
                    setCalendarOpen(false);
                  }
                }}
                disabled={(d) => d < firstDate || d > lastDate}
                initialFocus
              />
              {/* Footer matches the Signal-DS DatePicker: blue "Today" link + value. */}
              <div className="mt-2 flex items-center justify-between border-t border-[var(--surface-line)] pt-2 text-size-sm">
                <button
                  type="button"
                  onClick={() => {
                    setBlockDate(new Date());
                    setCalendarOpen(false);
                  }}
                  className="font-medium text-brand-primary hover:underline"
                >
                  Today
                </button>
                <span className="text-text-muted">{formatDate(state.blockDate)}</span>
              </div>
            </PopoverContent>
          </Popover>
        </div>

        {/* 60-day date strip. */}
        <BlockAvailabilityDateStrip
          selectedDate={state.blockDate}
          onDateChange={setBlockDate}
          disabled={isEditMode}
        />

        {/* Actor search + single-select — the provider grid, or its device
            sibling on a device block (PROD-2454). Edit mode adds a label
            above the selected card to match Flutter's visual structure on
            the existing-block view; the word follows the actor so the panel
            never calls a machine a provider. */}
        <div>
          {isEditMode && (
            <p className="text-xs font-medium text-text-muted mb-2">
              {isDeviceBlock ? 'Device' : 'Provider'}
            </p>
          )}
          {isDeviceBlock ? (
            <BlockAvailabilityDeviceSearch
              devices={devices}
              selectedDevice={state.actor}
              onSelect={selectActor}
              onClear={clearActor}
              isLoading={devicesLoading}
              disabled={isEditMode}
            />
          ) : (
            <BlockAvailabilityProviderSearch
              organizationId={organizationId}
              practiceId={practiceId}
              selectedDate={state.blockDate}
              selectedProvider={state.actor}
              onSelect={selectActor}
              onClear={clearActor}
              disabled={isEditMode}
            />
          )}
        </div>
        </fieldset>

        {/* Break form — only rendered once a provider is selected
            (Flutter `:243-260` same gate). Edit mode locks every field
            AND swaps to the view-only layout (no SHIFTS, plain text
            for start/end). BREAK section label only in edit mode. */}
        {state.actor && isEditMode && (
          <p className="text-xs font-medium text-text-muted pt-4">
            Break
          </p>
        )}
        {state.actor && (
          <BlockAvailabilityBreakForm
            actorId={state.actor.id}
            actorKind={state.actorKind}
            practiceId={practiceId}
            blockDate={state.blockDate}
            shiftKey={state.shiftKey}
            onChangeShift={(key) => setShiftKey(key)}
            startTime={state.startTime}
            endTime={state.endTime}
            comment={state.comment}
            onChangeStartTime={setStartTime}
            onChangeEndTime={setEndTime}
            onChangeComment={setComment}
            disabled={isSaving || isEditMode}
            viewOnly={isEditMode}
          />
        )}

        {/* Recurring toggle + picker — Flutter `block_availability_widget.dart:478-497`.
            Toggle ON creates the recurring object; OFF nulls it. */}
        {state.actor && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-text-primary">Recurring</span>
              <Switch
                aria-label="Toggle recurring"
                checked={state.recurring !== null}
                disabled={isSaving || isEditMode}
                onCheckedChange={setRecurringEnabled}
              />
            </div>
            {state.recurring && (
              <BlockAvailabilityRecurringPicker
                recurring={state.recurring}
                blockDate={state.blockDate}
                onPatch={setRecurring}
                onChangePeriod={setRecurringPeriod}
                onToggleDay={toggleRecurringDay}
                disabled={isSaving || isEditMode}
              />
            )}
          </div>
        )}

        {/* Unblock actions (when editing) — Flutter
            `block_availability_widget.dart:498-577`. Single-occurrence
            button always shown; recurring-series button only when the
            loaded block has a recurring rule. */}
        {isEditMode && canEditBlock && (
          <button
            type="button"
            onClick={() => setUnblockConfirm('single')}
            disabled={isUnblocking || isSaving}
            className="block mx-auto w-64 h-8 rounded-md border border-[var(--surface-line)] bg-[var(--surface-read)] text-base font-bold text-[var(--status-bad)] hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Unblock this availability
          </button>
        )}
        {isEditMode && canEditBlock && state.recurring && (
          <button
            type="button"
            onClick={() => setUnblockConfirm('recurring')}
            disabled={isUnblocking || isSaving}
            className="block mx-auto w-64 h-8 rounded-md border border-[var(--surface-line)] bg-[var(--surface-read)] text-base font-bold text-[var(--status-bad)] hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Unblock this and following
          </button>
        )}

        </>
        )}
      </div>

      {/* Unblock confirmation — copy verbatim from Flutter
          `block_availability_widget.dart:514-562`. Two variants:
          single-occurrence vs recurring-series. */}
      <AlertDialog
        open={unblockConfirm !== null}
        onOpenChange={(open) => !open && setUnblockConfirm(null)}
      >
        <AlertDialogContent className="sm:max-w-[var(--dialog-width-confirm)]">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {unblockConfirm === 'recurring'
                ? 'Do you want to unblock all the recurring availability?'
                : 'Do you want to unblock this availability?'}
            </AlertDialogTitle>
            <AlertDialogDescription className="[font:var(--type-body)] text-[var(--ink-secondary)]">
              {unblockConfirm === 'recurring'
                ? 'After you unblock, all the recurring slot will be available for booking appointments'
                : 'After you unblock, this slot will be available for booking appointments'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isUnblocking}>No</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmUnblock} disabled={isUnblocking}>
              {isUnblocking ? 'Unblocking...' : 'Yes, unblock'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Save confirmation — Flutter parity
          (`block_availability_widget.dart:213-244`).
          Copy is verbatim from the Dart source per CLAUDE.md
          "magic strings copied verbatim from Flutter constants". */}
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent className="sm:max-w-[var(--dialog-width-confirm)]">
          <AlertDialogHeader>
            <AlertDialogTitle>Do you want to block this availability?</AlertDialogTitle>
            <AlertDialogDescription className="[font:var(--type-body)] text-[var(--ink-secondary)]">
              Please make sure you cancel all the appointments booked under
              this time so that the patients are notified about this change.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSaving}>No</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmSave} disabled={isSaving}>
              {isSaving ? 'Saving...' : 'Yes, block'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Blockers dialog — extracted to its own presentational
          component so Storybook can snapshot it directly without
          mocking the save mutation. See
          `block-availability-blockers-dialog.tsx`. */}
      <BlockersConflictDialog
        blockers={blockers}
        onClose={() => setBlockers(null)}
      />
    </div>
  );
}
